import React, { useMemo, useState } from 'react';
import { Share2 } from 'lucide-react';
import {
  storySharesHistory, shareColor, fmtShare, barShareLine, SHARE_SERIES, YS_KEY, CF_SHARE_KEY, STORY_SHARES_TAG,
} from '../../lib/storySharesView';
import {
  actionStack, toggleHidden, fmtInt, fmtDay, fmtSpan, fmtRate, plural,
} from '../../lib/slotStatsView';
import { ChartCard, DataTableView, EmptyPanel } from './ChartKit';
import { ActionsStack } from './ActionsCharts';
import { Figure, NOT_RECORDED } from './R15Parts';

/*
 * r16n (2026-10-21), design D:/NecroWork/r15k10-cf-evidence/DESIGN.md 4 (lib/storySharesView.js has the contract and why this is a
 * card of its own): story shares by audience on Slot Stats - "Your story (everyone)" and "Close Friends" (the account's whole Close
 * Friends list; setting 1109 / 1110 sets the chance per share).
 *
 *   - figures over the page's range: all counted shares (with the average a day), each audience, and the Close Friends share as a
 *     percentage with a two-colour split bar (the 50 / 50 the owner asked about, read at a glance);
 *   - per day (per week at 90 days) the two audiences stacked, seamless in the page's StackSegment style (vivid, no gap inside a
 *     bar, a lighter "so far" bar), a clickable legend that hides / shows an audience, a tooltip with the day's total, both audiences
 *     and the Close Friends share, the average line, and a table twin;
 *   - honest gaps: a server before r16n (or its storyShareStats switch OFF) sends no fields and the card is not drawn; a slot with
 *     nothing recorded yet draws nothing either (one console line each); an audience not recorded reads "not recorded", never 0.
 * Instagram only (the page passes it only for an Instagram slot) and switch r16nUiEnabled.
 */

const logged = new Set();
/** One console line per page load and reason: why the card is not drawn for a slot. */
function logNotDrawn(slotName, why) {
  const line = `${STORY_SHARES_TAG} V3 Story shares card not drawn for ${slotName}: ${why}`;
  if (logged.has(line)) return;
  logged.add(line);
  // eslint-disable-next-line no-console
  console.info(line);
}

const listText = (labels) => (labels.length <= 1 ? labels.join('') : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`);

function Swatch({ color }) {
  return <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px] align-[-1px]" style={{ background: color }} aria-hidden />;
}

function SwatchLabel({ color, children }) {
  return <span className="inline-flex items-center gap-1.5"><Swatch color={color} />{children}</span>;
}

/** The Close Friends share of the period as a 100% bar: Your story on the left, Close Friends on the right, seamless. */
function SplitBar({ everyone, closeFriends }) {
  const all = everyone + closeFriends;
  if (!(all > 0)) return null;
  const cf = (closeFriends / all) * 100;
  return (
    <div className="mt-2 flex h-2 overflow-hidden rounded-full bg-white/[.06]" role="img" data-testid="story-shares-split-bar"
      aria-label={`${fmtInt(closeFriends)} of ${fmtInt(all)} story shares went to Close Friends, ${fmtInt(everyone)} to Your story`}>
      {everyone > 0 && <div className="h-full" style={{ width: `${100 - cf}%`, background: shareColor(YS_KEY) }} data-part="everyone" />}
      {closeFriends > 0 && <div className="h-full" style={{ width: `${cf}%`, background: shareColor(CF_SHARE_KEY) }} data-part="closeFriends" />}
    </div>
  );
}

export default function StoryShareCard({ slotName, statsRaw, range = 30, now }) {
  const [hidden, setHidden] = useState([]);
  const h = useMemo(() => storySharesHistory(statsRaw, range, now || new Date()), [statsRaw, range, now]);
  if (!h.sent) {
    logNotDrawn(slotName, 'this server sends no story-share fields (a server before r16n, or its storyShareStats switch is OFF)');
    return null;
  }
  if (!h.present) {
    logNotDrawn(slotName, 'no story share is recorded for this slot yet (counted from worker 15k.10)');
    return null;
  }
  const per = h.weekly ? 'week' : 'day';
  const span = h.days.length ? fmtSpan(h.days[0], h.days[h.days.length - 1]) : null;
  const t = h.totals;
  const series = h.keys.map((k) => ({ key: k, label: SHARE_SERIES.find((s) => s.key === k).label }));
  const stack = actionStack(h.bars, h.keys, hidden, { weekly: h.weekly });
  const late = h.lateStarts.length && !h.allZero
    ? Object.values(h.lateStarts.reduce((g, l) => {
      (g[l.date] = g[l.date] || { date: l.date, labels: [] }).labels.push(l.label);
      return g;
    }, {})) : [];
  const cfSeries = h.series.find((s) => s.key === CF_SHARE_KEY);
  const ysSeries = h.series.find((s) => s.key === YS_KEY);

  const table = !h.allZero ? (
    <DataTableView caption={`Story shares per ${per} by audience`} testid="story-shares-datatable"
      columns={[
        { key: 'date', label: h.weekly ? 'Week' : 'Date', render: (r) => (h.weekly ? fmtSpan(r.from, r.to) : fmtDay(r.date, 'long')) },
        ...series.map((s) => ({
          key: s.key, label: s.label, align: 'right', render: (r) => (r.parts[s.key] === null ? '-' : fmtInt(r.parts[s.key])),
        })),
        {
          key: 'total', label: 'Total', align: 'right',
          render: (r) => (r.value === null ? NOT_RECORDED : `${fmtInt(r.value)}${r.running ? ' (so far)' : ''}`),
        },
        ...(h.keys.length === 2 ? [{
          key: 'cfShare', label: 'Close Friends share', align: 'right',
          render: (r) => {
            const cf = r.parts[CF_SHARE_KEY];
            const ys = r.parts[YS_KEY];
            if (cf === null || ys === null || !(cf + ys > 0)) return '-';
            return fmtShare(cf / (cf + ys));
          },
        }] : []),
      ]}
      rows={[...h.bars].reverse()} />
  ) : null;

  const chart = h.allZero ? (
    <EmptyPanel testid="story-shares-chart-empty">No story shares counted in {span}: every recorded {per} is 0.</EmptyPanel>
  ) : (
    <ActionsStack stack={stack} series={series} weekly={h.weekly} late={late} testid="story-shares-chart"
      onToggle={(k) => setHidden(toggleHidden(stack.hidden, k, h.keys))} onShowAll={() => setHidden([])}
      chartProps={{
        colorOf: shareColor, segKeys: h.keys, yTitle: `Story shares per ${per}`, noun: 'audience', lateWord: 'audience', height: 230,
        tipProps: { colorOf: shareColor, totalNoun: 'story shares', extraFooter: barShareLine },
      }}
      legendProps={{ colorOf: shareColor, noun: 'audiences', label: 'Audiences: click to hide or show', testid: 'story-shares-legend' }} />
  );

  const footer = [];
  if (!h.allZero && h.partial) {
    footer.push(
      <p key="partial" data-testid="story-shares-partial-note">
        {h.weekly
          ? 'Lighter bars are incomplete weeks: the week holding today (so far), or fewer than 7 recorded days.'
          : 'The lighter bar is today so far: the day is not over, so it is left out of the average.'}
      </p>,
    );
  }
  late.forEach((l) => footer.push(
    <p key={`late-${l.date}`} data-testid="story-shares-late-note">
      {listText(l.labels)} {l.labels.length === 1 ? 'is' : 'are'} recorded from {fmtDay(l.date, 'medium')}; the days before it do not
      include {l.labels.length === 1 ? 'it' : 'them'}.
    </p>,
  ));
  if (h.keys.length === 1) {
    const missing = SHARE_SERIES.find((s) => s.key !== h.keys[0]);
    footer.push(
      <p key="one" data-testid="story-shares-one-audience">
        Only {series[0].label} shares are recorded for this slot; {missing.label} is {NOT_RECORDED}.
      </p>,
    );
  }
  if (h.day0Known && h.day0 !== h.today) {
    footer.push(
      <p key="day0" data-testid="story-shares-counter-day-note">
        The slot&apos;s counters are on {fmtDay(h.day0, 'medium')}: it has not started a newer day since.
      </p>,
    );
  }
  footer.push(
    <p key="how" data-testid="story-shares-how">
      A share counts as Close Friends only when the worker saw Close Friends selected, and Your story not, before it tapped Share;
      every other share (feed shares, 1314, included) counts as Your story. A share that rolled Close Friends but could not select it
      (an empty list, a set-up screen) is skipped and counted in neither: it is never posted to everyone instead.
    </p>,
  );

  const cfValue = cfSeries && cfSeries.present ? t.closeFriends : null;
  const ysValue = ysSeries && ysSeries.present ? t.everyone : null;
  let shareCaption = null;
  if (t.cfShare !== null) shareCaption = `${fmtInt(t.closeFriends)} of ${fmtInt(t.all)} shares${span ? `, ${span}` : ''}`;
  else if (t.all === 0) shareCaption = 'No share counted in this range';
  else if (h.keys.length === 1) shareCaption = `${SHARE_SERIES.find((s) => s.key !== h.keys[0]).label} is not recorded`;

  return (
    <ChartCard icon={Share2} title="Story shares" testid="story-shares-card"
      subtitle={`Posts shared to this account's story, by audience${span ? `, ${span}` : ''}. Settings 1109 / 1110 set the chance a share `
        + 'goes to Close Friends.'}
      table={table}
      controls={(
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <Figure label="Story shares" testid="story-shares-total" value={t.all === null ? null : fmtInt(t.all)}
            caption={[
              span ? `in ${plural(h.days.length, 'day')}` : null,
              !h.weekly && t.avg !== null ? `${fmtRate(t.avg, { signed: false })} a day on average` : null,
            ].filter(Boolean).join(' · ') || null} />
          <Figure label={<SwatchLabel color={shareColor(YS_KEY)}>Your story (everyone)</SwatchLabel>} testid="story-shares-everyone"
            value={ysValue === null ? null : fmtInt(ysValue)} caption="Seen by everyone who can see the account's stories" />
          <Figure label={<SwatchLabel color={shareColor(CF_SHARE_KEY)}>Close Friends</SwatchLabel>} testid="story-shares-cf"
            value={cfValue === null ? null : fmtInt(cfValue)} caption="The whole Close Friends list, nobody picked" />
          <Figure label="Close Friends share" testid="story-shares-cf-share" value={fmtShare(t.cfShare)} caption={shareCaption}>
            {t.cfShare !== null && <SplitBar everyone={t.everyone} closeFriends={t.closeFriends} />}
          </Figure>
        </div>
      )}
      footer={footer}>
      {chart}
    </ChartCard>
  );
}
